import GroupChannel from '@sendbird/uikit-react/GroupChannel';
import Message from '@sendbird/uikit-react/GroupChannel/components/Message';
import MessageSearch from '@sendbird/uikit-react/MessageSearch';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { Private, XStack } from 'smoothie';

import { useInitLocalMessageDrafts } from '../hooks/useInitLocalMessageDrafts';
import { useShowGuideMessaging } from '../hooks/useShowGuideMessaging';
import type { GuideMessagingDto, SearchItem } from '../types/GuideMessaging';
import ChatHeader from './ChatHeader';
import { ChatInput } from './ChatInput';

interface ChatProps {
    guideMessaging: GuideMessagingDto;
    searchItem?: SearchItem;
}

const Chat = ({ guideMessaging, searchItem }: ChatProps) => {
    const showGuideMessagingModal = useShowGuideMessaging();
    const { draftMessage, setDraftMessage, clearDraftMessage } =
        useInitLocalMessageDrafts();
    const [shouldShowSearch, setShouldShowSearch] = useState(false);
    const [searchItemState, setSearchItemState] = useState<SearchItem | null>(
        searchItem ?? null,
    );

    const { currentChat } = guideMessaging;
    const {
        channel: { channelUrl },
    } = currentChat;

    const close = () => {
        showGuideMessagingModal(false);
    };

    const ChannelHeader = useMemo(
        () => (
            <ChatHeader
                onPressHeaderLeft={close}
                onPressHeaderRight={() => {
                    setShouldShowSearch(true);
                }}
            />
        ),
        [close],
    );

    return (
        <XStack height="100%">
            {sendbirdCssOverrides}
            {!shouldShowSearch ? (
                <GroupChannel
                    channelUrl={channelUrl}
                    disableUserProfile={true} // Disables modal popup when clicking any user avatar in channel.
                    replyType="NONE" // Disables reply/thread functionality.
                    isMultipleFilesMessageEnabled={true} // Only enable if all clients can render multiple file messages.
                    renderChannelHeader={() => ChannelHeader}
                    startingPoint={searchItemState?.startingPoint}
                    renderMessage={(props) => (
                        <Private>
                            <Message {...props} />
                        </Private>
                    )}
                    renderMessageInput={() => (
                        <Private>
                            <ChatInput
                                // @ts-expect-error 'initialMessage' is actually valid for ./ChatInput.web.tsx
                                initialMessage={draftMessage}
                                onMessageChange={setDraftMessage}
                            />
                        </Private>
                    )}
                    onBeforeSendUserMessage={(params) => {
                        clearDraftMessage();
                        return params;
                    }}
                />
            ) : (
                <MessageSearch
                    channelUrl={channelUrl}
                    onResultClick={(message) => {
                        setSearchItemState({
                            startingPoint: message.createdAt,
                        });
                        setShouldShowSearch(false);
                    }}
                    onCloseClick={() => {
                        setShouldShowSearch(false);
                    }}
                />
            )}
        </XStack>
    );
};

export default Chat;

// Override styling of Sendbird UIKit components.
const sendbirdCssOverrides = (
    <style type="text/css">
        {`
            .sendbird-conversation {
                border-width: 0 !important; /* Remove border because this is in a modal */
            }
            .sendbird-conversation__footer {
                padding-bottom: 16px !important; /* Right-size padding to be uniform */
            }
            .sendbird-message-hoc__message-content.sendbird-message-content {
                width: 100% !important; /* Unsure why, but the message content was too narrow */
            }
            .sendbird-message-status .sendbird-message-status__icon.sendbird-icon {
                display: none !important; /* We don't want to show delivery receipts or read receipts */
            }
            .sendbird-message-search-pannel { /* 'pannel' is a typo in the source class name */
                width: 100% !important; /* Otherwise it uses a fixed width unnecessarily that we'd have to handle dynamically */
            }
            .sendbird-message-search-pannel .sendbird-message-search { /* 'pannel' is a typo in the source class name */
                overflow-y: auto !important; /* Only show scrollbar when needed */
            }
            .sendbird-msg-hoc .sendbird-msg-hoc .sendbird-separator {
                display: none !important; /* When using our wrapped/custom Message, this separator is a duplicate */
            }
            
            /**
             * Hide the message content menu kebab button if there are no available options. This state occurs on received/incoming file messages.
             */
            .sendbird-message-content__middle:has(> .sendbird-message-content__middle__body-container > .sendbird-thumbnail-message-item-body) + .sendbird-message-content__right {
                display: none !important;
            }
            
            /** 
             * Ensure the File Viewer, Delete, and any other Sendbird-specific modals render above the guide messaging modal.
             */
             .sendbird-modal-root {
                z-index: 10 !important;
             } 
        `}
    </style>
);
